import { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { nb, enUS } from 'date-fns/locale';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useBlogPosts, useDeleteBlogPost, useUpdateBlogPost } from '@/hooks/useBlog';
import { BlogPostEditor } from './components/BlogPostEditor';
import { useTranslation, useLanguage } from '@/features/i18n';
import type { BlogPost } from '@/types/database';

export function BlogAdminPage() {
  const [page, setPage] = useState(1);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<BlogPost | null>(null);

  const { data, isLoading } = useBlogPosts({ page, includeUnpublished: true });
  const deletePost = useDeleteBlogPost();
  const updatePost = useUpdateBlogPost();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const dateLocale = language === 'no' ? nb : enUS;

  const handleDelete = async () => {
    if (postToDelete) {
      await deletePost.mutateAsync(postToDelete.id);
      setDeleteModalOpen(false);
      setPostToDelete(null);
    }
  };

  const handleTogglePublish = async (post: BlogPost) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    await updatePost.mutateAsync({
      id: post.id,
      status: newStatus,
      published_at: newStatus === 'published' ? new Date().toISOString() : null,
    });
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setEditingPost(null);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingPost(null);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO title={t.admin.blog.title} noIndex />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.blog.title}</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t.admin.blog.subtitle}
            </p>
          </div>
          <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
            {t.admin.blog.newPost}
          </Button>
        </div>

        {/* Posts list */}
        {data?.posts && data.posts.length > 0 ? (
          <div className="space-y-4">
            {data.posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      {post.cover_image_url && (
                        <img
                          src={post.cover_image_url}
                          alt={post.title}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-neutral-900 dark:text-white truncate">
                            {post.title}
                          </h3>
                          <Badge variant={post.status === 'published' ? 'success' : 'warning'}>
                            {post.status === 'published' ? t.admin.blog.published : t.admin.blog.draft}
                          </Badge>
                        </div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-1">
                          {post.excerpt || t.admin.common.noDescription}
                        </p>
                        <div className="mt-1 text-xs text-neutral-500">
                          {post.category && <span>{post.category} &middot; </span>}
                          {post.published_at
                            ? format(new Date(post.published_at), 'PPP', { locale: dateLocale })
                            : t.admin.common.notPublished}
                          {' &middot; '}{post.reading_time} {t.admin.common.readingTime}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTogglePublish(post)}
                        title={post.status === 'published' ? t.admin.common.unpublish : t.admin.common.publish}
                      >
                        {post.status === 'published' ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(post)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setPostToDelete(post);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {t.admin.common.previous}
                </Button>
                <span className="flex items-center px-4 text-neutral-600 dark:text-neutral-400">
                  {t.admin.common.pageOf.replace('{page}', String(page)).replace('{total}', String(data.totalPages))}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page === data.totalPages}
                >
                  {t.admin.common.next}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title={t.admin.blog.noPosts}
            description={t.admin.blog.noPostsDesc}
            action={
              <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
                {t.admin.blog.createFirst}
              </Button>
            }
          />
        )}
      </div>

      {/* Editor modal */}
      <Modal
        isOpen={editorOpen}
        onClose={handleEditorClose}
        title={editingPost ? t.admin.blog.editPost : t.admin.blog.newPost}
        size="full"
      >
        <BlogPostEditor post={editingPost} onClose={handleEditorClose} />
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t.admin.blog.deletePost}
      >
        <div className="p-6">
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {t.admin.common.confirmDelete.replace('{name}', postToDelete?.title || '')} {t.admin.common.cannotUndo}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deletePost.isPending}>
              {t.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
